import { FlexRow } from '@/components/common/Flex';
import { AlertCircle } from '@/components/common/icons';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Header, Paragraph, Subheader } from '@/components/common/Typography';
import { FinancialEntityIcon } from '@/components/financialEntities/FinancialEntityIcon';
import usePlaid from '@/components/plaid/usePlaid';
import { Box, Divider, Spacer, Text, Tooltip, VStack } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { getLinkedInstitutionRowSubtitle } from '@common/react/accountLinking';
import { UsePlaidOptions } from '@common/react/aggregators';
import { Institution, useInstitutions } from '@common/react/financialEntities';
import { useModal } from '@ebay/nice-modal-react';
import { useState } from 'react';
import { Button } from '../common/Button';
import { FlexCol } from '../common/Flex';

export interface ConnectAccountInstitutionListViewProps {
    plaidOptions: UsePlaidOptions;
    showExistingInstitutions: boolean;
}

const ConnectAccountInstitutionListView = ({
    plaidOptions,
    showExistingInstitutions: showExistingInstitutionsInitial = false,
}: ConnectAccountInstitutionListViewProps) => {
    const [showExistingInstitutions, setShowExistingInstitutions] = useState(
        showExistingInstitutionsInitial
    );
    const modal = useModal();
    const { open } = usePlaid({
        ...plaidOptions,
        onComplete: () => {
            plaidOptions.onComplete?.();
            modal.remove();
        },
    });

    const buttonProps = {
        h: 12,
        w: 200,
        size: 'sm',
    };

    return (
        <FlexCol gap={4}>
            <Header>Connect an account</Header>
            <Paragraph color="textMuted">
                Connect to a new financial institution, or add new accounts to
                your existing institutions.
            </Paragraph>
            {!showExistingInstitutions && (
                <FlexRow align="center" justify="space-between" mt={4}>
                    <Button
                        {...buttonProps}
                        onClick={open}
                        onClickEvent="Connect New Institution Button Clicked"
                    >
                        Connect to a new institution
                    </Button>
                    <Button
                        {...buttonProps}
                        onClick={() => {
                            setShowExistingInstitutions(true);
                        }}
                        variant="outline"
                        colorScheme="gray"
                        onClickEvent="Update Existing Institution Button Clicked"
                    >
                        Update an existing institution
                    </Button>
                </FlexRow>
            )}

            {showExistingInstitutions && (
                <InstitutionList plaidOptions={plaidOptions} />
            )}
        </FlexCol>
    );
};

const InstitutionList = ({
    plaidOptions,
}: {
    plaidOptions: UsePlaidOptions;
}) => {
    const institutions = useInstitutions();

    const connectedInstitutions = institutions.filter(
        (institutionRow) => !institutionRow.isDisconnectedOrError
    );
    const disconnectedInstitutions = institutions.filter(
        (institutionRow) => institutionRow.isDisconnectedOrError
    );

    return (
        <FlexCol w="100%" mt={8}>
            {disconnectedInstitutions.length > 0 && (
                <>
                    <Subheader mb={4}>Disconnected Institutions</Subheader>
                    <VStack align="stretch" divider={<Divider />} spacing={0}>
                        {disconnectedInstitutions.map((institutionRow) => (
                            <InstitutionRow
                                key={institutionRow.financialEntity.id}
                                institutionRow={institutionRow}
                                plaidOptions={plaidOptions}
                            />
                        ))}
                    </VStack>
                    <Divider mt={0} mb={8} />
                </>
            )}
            {connectedInstitutions.length > 0 && (
                <>
                    <Subheader mb={4}>Connected Institutions</Subheader>
                    <VStack align="stretch" divider={<Divider />} spacing={0}>
                        {connectedInstitutions.map((institutionRow) => (
                            <InstitutionRow
                                key={institutionRow.financialEntity.id}
                                institutionRow={institutionRow}
                                plaidOptions={plaidOptions}
                            />
                        ))}
                    </VStack>
                    <Divider mt={0} mb={8} />
                </>
            )}
        </FlexCol>
    );
};

const InstitutionRow = ({
    institutionRow,
    plaidOptions,
}: {
    institutionRow: Institution;
    plaidOptions: UsePlaidOptions;
}) => {
    const modal = useModal();
    // It may seem odd that we're using a single financial entity to represent
    // an institution, but doing this allows us to easily reuse all of the
    // FE-specific logic for Plaid relinking and showing the correct icon
    // for the FE's institution.
    const {
        financialEntity,
        sharedCount,
        privateCount,
        isDisconnectedOrError,
    } = institutionRow;
    const { open } = usePlaid({
        ...plaidOptions,
        financialEntity: {
            id: financialEntity.id,
            type: financialEntity.type,
            linkStatus: financialEntity.linkStatus,
        },
        onComplete: () => {
            plaidOptions.onComplete?.();
            modal.remove();
        },
    });

    assert(financialEntity.institution);

    return (
        <FlexRow
            key={financialEntity.id}
            align="center"
            gap={3}
            fontSize="md"
            py={3}
            px={2}
            mx={-2}
            {...hoverStyleProps({
                bg: 'gray.100',
            })}
            onClick={open}
        >
            <FinancialEntityIcon fe={financialEntity} />
            <FlexCol>
                {financialEntity.institution.name}
                <Text fontSize="sm" color="gray.400">
                    {getLinkedInstitutionRowSubtitle({
                        sharedCount,
                        privateCount,
                    })}
                </Text>
            </FlexCol>
            <Spacer />
            {isDisconnectedOrError && (
                <Tooltip label="The connection to this institution has been lost">
                    {/* Box wrapper is required here for tooltip to be placed correctly */}
                    <Box>
                        <AlertCircle color="red.300" />
                    </Box>
                </Tooltip>
            )}
        </FlexRow>
    );
};

export default ConnectAccountInstitutionListView;
